"use client";

import { FC, useEffect, useState } from "react";
import Link from "next/link";
import clsx from "clsx";
import { Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ArrowRight, Calendar, FileText, Zap } from "react-feather";

import LeadCaptureForm from "@/components/forms/LeadCaptureForm";
import { trackEvent } from "@/utils/analytics";

// ----------------------------------------------------------------------------
// Sticky conversion bar for the pentest / VAPT landing pages.
//
// Pinned to the bottom of the viewport on mobile and bottom-right on desktop.
// Opens the LeadCaptureForm in a modal so the visitor converts without ever
// leaving the page (the previous CTAs navigated away to /contact-us). Carries
// the two low-friction offers (quote, sample report) plus a tertiary
// "Book a Discovery Call" that links to /schedule.
//
// Appears after the user scrolls past the hero so it doesn't cover the
// above-the-fold inline form.
// ----------------------------------------------------------------------------

type IStickyLeadBarProps = {
  /** e.g. "pentest" | "vapt" — tags CTA events by page variant. */
  variant?: string;
};

const StickyLeadBar: FC<IStickyLeadBarProps> = ({ variant = "pentest" }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [intent, setIntent] = useState<"quote" | "sample-report">("quote");
  const [visible, setVisible] = useState(false);

  // Reveal after the user scrolls past the hero so the bar never covers the
  // above-the-fold content or the inline lead card.
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openWith = (next: "quote" | "sample-report", label: string) => {
    setIntent(next);
    trackEvent("cta_click", {
      cta_label: label,
      cta_location: "sticky_bar",
      page_variant: variant,
    });
    open();
  };

  return (
    <>
      {/* Sticky bar — full-width on mobile, compact pill bottom-right on lg+.
          Slides in once past the hero. */}
      <div
        className={clsx(
          "fixed inset-x-0 bottom-0 z-40 border-t border-black-100 bg-white/95 px-4 py-3 shadow-[0_-8px_24px_-12px_rgba(31,42,73,0.18)] backdrop-blur transition-all duration-300 lg:inset-x-auto lg:bottom-6 lg:right-6 lg:rounded-2xl lg:border lg:px-5 lg:py-4",
          visible
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-full opacity-0 lg:translate-y-4",
        )}
      >
        <div className="mx-auto flex max-w-3xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-center sm:gap-3 lg:max-w-none">
          <p className="hidden font-bricolage-grotesque text-sm font-medium text-black-900 lg:block">
            Ready to scope your pentest?
          </p>
          <button
            type="button"
            onClick={() => openWith("quote", "Get a Pentest Quote")}
            className="group inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#FB441B] px-5 font-bricolage-grotesque text-sm font-medium text-white shadow-sm transition-all hover:bg-[#E03A17] hover:shadow-[0_10px_25px_rgba(251,68,27,0.3)]"
          >
            <Zap size={15} />
            Get a Pentest Quote
            <ArrowRight
              size={15}
              className="transition-transform duration-200 group-hover:translate-x-0.5"
            />
          </button>
          <button
            type="button"
            onClick={() => openWith("sample-report", "Get a Sample Report")}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-primary-200 bg-white px-5 font-bricolage-grotesque text-sm font-medium text-primary-900 transition-all hover:border-primary-300 hover:shadow-sm"
          >
            <FileText size={15} />
            Get a Sample Report
          </button>
          <Link
            href="/schedule"
            onClick={() =>
              trackEvent("cta_click", {
                cta_label: "Book a Discovery Call",
                cta_location: "sticky_bar",
                page_variant: variant,
              })
            }
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full px-4 font-bricolage-grotesque text-sm font-medium text-black-700 transition-colors hover:text-primary-900"
          >
            <Calendar size={15} />
            <span className="whitespace-nowrap">Book a Discovery Call</span>
          </Link>
        </div>
      </div>

      {/* Lead form modal — conversion happens on-page. */}
      <Modal
        opened={opened}
        onClose={close}
        centered
        radius="lg"
        size="md"
        zIndex={9999}
        title={
          <span className="font-bricolage-grotesque text-h7 font-medium text-black-900">
            {intent === "quote"
              ? "Get your pentest quote"
              : "Get a sample report"}
          </span>
        }
        classNames={{ body: "pb-6" }}
      >
        <p className="mb-4 font-darker-grotesque text-base font-normal text-black-700">
          {intent === "quote"
            ? "Tell us what needs testing. A CERT-In empanelled engineer replies within one business day with a fixed-scope quote."
            : "See exactly what our reports look like — redacted findings, severity ratings, and remediation guidance."}
        </p>
        <LeadCaptureForm intent={intent} onSuccess={close} compact />
      </Modal>
    </>
  );
};

export default StickyLeadBar;
